import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Edit3 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const blank = {
  name: "", slug: "", logo: "", category: "", description: "",
  affiliate_network: "Direct", tracking_url: "", commission_rate: 5,
  customer_cashback_pct: 50, platform_pct: 50, referral_l1_pct: 20, referral_l2_pct: 5,
  cookie_duration_days: 30, payout_terms_days: 30, trust_score: 90,
  status: "active", featured: false,
};

export default function MerchantsAdmin() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);

  const load = async () => {
    const { data } = await api.get("/catalog/merchants?limit=200");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => { setEditing(null); setForm(blank); setOpen(true); };
  const startEdit = (m) => { setEditing(m); setForm({ ...blank, ...m }); setOpen(true); };

  const save = async () => {
    try {
      const payload = {
        ...form,
        commission_rate: parseFloat(form.commission_rate),
        customer_cashback_pct: parseFloat(form.customer_cashback_pct),
        platform_pct: parseFloat(form.platform_pct),
        referral_l1_pct: parseFloat(form.referral_l1_pct),
        referral_l2_pct: parseFloat(form.referral_l2_pct),
        cookie_duration_days: parseInt(form.cookie_duration_days),
        payout_terms_days: parseInt(form.payout_terms_days),
        trust_score: parseFloat(form.trust_score),
      };
      if (editing) {
        await api.put(`/catalog/merchants/${editing.id}`, payload);
        toast.success("Merchant updated");
      } else {
        await api.post("/catalog/merchants", payload);
        toast.success("Merchant created");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete merchant?")) return;
    await api.delete(`/catalog/merchants/${id}`);
    load();
  };

  return (
    <div data-testid="adm-merchants-page">
      <PageHeader
        eyebrow="Catalog"
        title="Merchant management"
        subtitle="Add, edit, and configure affiliate merchants."
        action={<Button onClick={startCreate} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full" data-testid="adm-new-merchant"><Plus className="w-4 h-4 mr-2" /> New merchant</Button>}
      />

      <Card className="border-gray-200 shadow-none rounded-2xl">
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="border-b border-gray-200 bg-gray-50">
              <tr className="text-left">
                <th className="px-5 py-3 font-semibold">Merchant</th>
                <th className="px-5 py-3 font-semibold">Category</th>
                <th className="px-5 py-3 font-semibold">Commission</th>
                <th className="px-5 py-3 font-semibold">Trust</th>
                <th className="px-5 py-3 font-semibold">Status</th>
                <th className="px-5 py-3 font-semibold w-24">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((m) => (
                <tr key={m.id} className="border-b border-gray-100" data-testid={`adm-mrow-${m.slug}`}>
                  <td className="px-5 py-3 flex items-center gap-2">
                    {m.logo && <img src={m.logo} alt={m.name} className="w-7 h-7 rounded object-contain bg-gray-50 p-0.5 border border-gray-100" onError={(e) => (e.target.style.display = "none")} />}
                    <span className="font-medium">{m.name}</span>
                    {m.featured && <Badge className="bg-amber-50 text-amber-800 border border-amber-200">Featured</Badge>}
                  </td>
                  <td className="px-5 py-3 capitalize text-gray-600">{m.category}</td>
                  <td className="px-5 py-3">{m.commission_rate}%</td>
                  <td className="px-5 py-3">{m.trust_score}</td>
                  <td className="px-5 py-3"><Badge variant="outline" className={m.status === "active" ? "border-emerald-200 text-emerald-800 bg-emerald-50" : ""}>{m.status}</Badge></td>
                  <td className="px-5 py-3 flex gap-1">
                    <button onClick={() => startEdit(m)} className="p-1.5 text-gray-500 hover:text-emerald-700"><Edit3 className="w-4 h-4" /></button>
                    <button onClick={() => remove(m.id)} className="p-1.5 text-gray-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit merchant" : "New merchant"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Slug</Label><Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/\s+/g, "-") })} /></div>
            <div className="col-span-2"><Label>Logo URL</Label><Input value={form.logo} onChange={(e) => setForm({ ...form, logo: e.target.value })} /></div>
            <div><Label>Category</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></div>
            <div><Label>Affiliate network</Label><Input value={form.affiliate_network} onChange={(e) => setForm({ ...form, affiliate_network: e.target.value })} /></div>
            <div className="col-span-2"><Label>Tracking URL</Label><Input value={form.tracking_url} onChange={(e) => setForm({ ...form, tracking_url: e.target.value })} /></div>
            <div className="col-span-2"><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div><Label>Commission rate (%)</Label><Input type="number" step="0.5" value={form.commission_rate} onChange={(e) => setForm({ ...form, commission_rate: e.target.value })} /></div>
            <div><Label>Trust score</Label><Input type="number" min="0" max="100" value={form.trust_score} onChange={(e) => setForm({ ...form, trust_score: e.target.value })} /></div>
            <div><Label>Customer cashback %</Label><Input type="number" value={form.customer_cashback_pct} onChange={(e) => setForm({ ...form, customer_cashback_pct: e.target.value })} /></div>
            <div><Label>Platform %</Label><Input type="number" value={form.platform_pct} onChange={(e) => setForm({ ...form, platform_pct: e.target.value })} /></div>
            <div><Label>Referral L1 %</Label><Input type="number" value={form.referral_l1_pct} onChange={(e) => setForm({ ...form, referral_l1_pct: e.target.value })} /></div>
            <div><Label>Referral L2 %</Label><Input type="number" value={form.referral_l2_pct} onChange={(e) => setForm({ ...form, referral_l2_pct: e.target.value })} /></div>
            <div><Label>Cookie days</Label><Input type="number" value={form.cookie_duration_days} onChange={(e) => setForm({ ...form, cookie_duration_days: e.target.value })} /></div>
            <div><Label>Payout days</Label><Input type="number" value={form.payout_terms_days} onChange={(e) => setForm({ ...form, payout_terms_days: e.target.value })} /></div>
            <div className="col-span-2 flex gap-4">
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} /> Featured</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.status === "active"} onChange={(e) => setForm({ ...form, status: e.target.checked ? "active" : "inactive" })} /> Active</label>
            </div>
          </div>
          <DialogFooter><Button onClick={save} className="bg-emerald-700 hover:bg-emerald-800 text-white">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
